import { UserStatus, PaymentCategory, DbRef, OfficeType } from '@caresend/types';
import { initOrderFlowSettings, initialPhone, initElementTimestamp, getOfficePath, hasStatus, isNullish, errorSuffix, formatFullName, nullishFilter, arrayToObj, deduplicateArray } from '@caresend/utils';
import update from 'immutability-helper';
import { decryptData } from '../../../database/encryption/decrypt.js';
import 'firebase/auth';
import { handleSetupIntentSucceededRequest, createPaymentOrSetupIntentRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { firebaseBind, dbGroupSet, dbUpdate, firebaseUnbind } from '../../../database/firebaseConfig/methods.js';
import { reportException } from '../../../functions/sentry.js';
import { toastSuccess, toastErrorAndReport } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { initExtendedModule } from '../helpers.js';
import { initOfficeAlgoliaState, initOfficeLocalOrderIDs, initOfficeBillingSettings, initOfficeContactSettings, clearAlgoliaFilters, initOfficeAlgoliaOrdersFilter } from './init.js';
import { initLabResultsRecipients } from './methods.js';

// Note: The office module is specifically for pro. It has been migrated to
// ui-components, to support pro-specific logic in shared components such as
// the NavBar showing a prescriber's office and potential pro-specific
// behaviour in the order flow. For logic that is not specific to prescribers
// and assistants, use `offices`.
const officeState = {
    algolia: initOfficeAlgoliaState(),
    orderIDs: null,
    office: null,
    officeLoadError: null,
    officeLoading: true,
    localOrderIDs: initOfficeLocalOrderIDs(),
    localSettings: {
        billing: initOfficeBillingSettings(),
        contact: initOfficeContactSettings(),
        orderFlow: initOrderFlowSettings(),
    },
};
const officeMutations = {
    'office/INIT_BILLING_SETTINGS': async (state, settings) => {
        state.localSettings.billing = settings;
    },
    'office/INIT_CONTACT_SETTINGS': (state) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        state.localSettings.contact = update(initOfficeContactSettings(), {
            cliaID: { $set: (_b = (_a = state.office) === null || _a === void 0 ? void 0 : _a.cliaID) !== null && _b !== void 0 ? _b : null },
            initialized: { $set: true },
            location: { $set: (_d = (_c = state.office) === null || _c === void 0 ? void 0 : _c.location) !== null && _d !== void 0 ? _d : null },
            name: { $set: (_f = (_e = state.office) === null || _e === void 0 ? void 0 : _e.info.name) !== null && _f !== void 0 ? _f : null },
            phoneValid: { $set: !!((_g = state.office) === null || _g === void 0 ? void 0 : _g.info.phone) },
            phone: { $set: (_j = (_h = state.office) === null || _h === void 0 ? void 0 : _h.info.phone) !== null && _j !== void 0 ? _j : initialPhone() },
        });
    },
    'office/INIT_ORDER_FLOW_SETTINGS': (state) => {
        var _a, _b;
        state.localSettings.orderFlow
            = (_b = (_a = state.office) === null || _a === void 0 ? void 0 : _a.orderSettings.orderFlowSettings) !== null && _b !== void 0 ? _b : initOrderFlowSettings();
    },
    'office/RESET_ALGOLIA_PAGINATION': (state, name) => {
        state.algolia[name].pages = null;
        state.algolia[name].pagesLoaded = null;
    },
    'office/RESET_OFFICE': (state) => {
        state.office = null;
        state.algolia = initOfficeAlgoliaState();
        state.officeLoading = true;
    },
    'office/SET_ALGOLIA_KEY': (state, { name, keyData }) => {
        state.algolia[name].keyData = keyData;
    },
    'office/SET_ALGOLIA_LOADING': (state, { name, loading }) => {
        state.algolia[name].loading = loading;
    },
    'office/SET_ALGOLIA_PAGINATION': (state, { name, pages, pagesLoaded }) => {
        state.algolia[name].pages = pages;
        state.algolia[name].pagesLoaded = pagesLoaded;
    },
    'office/SET_ALGOLIA_ORDERS_CURRENT_PAST_FILTER': (state, filter) => {
        state.algolia.orders.currentPastFilter = filter;
    },
    'office/SET_ALGOLIA_ORDERS_ACTION_REQUIRED_FILTER': (state, filter) => {
        state.algolia.orders.actionRequiredFilter = filter;
    },
    'office/SET_ALGOLIA_ORDERS_SPECIMEN_BAG_FILTER': (state, filter) => {
        state.algolia.orders.specimenBagID = filter;
    },
    'office/SET_ALGOLIA_ORDERS_PATIENT_FILTER': (state, patientID) => {
        state.algolia.orders.patientID = patientID;
    },
    'office/SET_ALGOLIA_ORDERS_PRESCRIBER_FILTER': (state, prescriberID) => {
        state.algolia.orders.prescriberID = prescriberID;
    },
    'office/SET_ALGOLIA_ORDERS_LAB_ORDER_ID_FILTER': (state, labOrderID) => {
        state.algolia.orders.labOrderID = labOrderID;
    },
    'office/CLEAR_ALGOLIA_ORDERS_FILTERS': (state) => {
        state.algolia.orders = clearAlgoliaFilters();
    },
    'office/RESET_ALGOLIA_ORDERS_FILTERS': (state) => {
        state.algolia.orders = initOfficeAlgoliaOrdersFilter();
    },
    'office/SET_ALGOLIA_ORDERS_OFFICE_FILTER': (state, officeID) => {
        state.algolia.orders.officeID = officeID;
    },
    'office/SET_ALGOLIA_ORDERS_STATUS_FILTER': (state, statusFilters) => {
        state.algolia.orders.statusFilters = statusFilters;
    },
    'office/SET_ALGOLIA_ORDERS_STATUS_FILTER_TYPE': (state, statusFilterType) => {
        state.algolia.orders.statusFilterType = statusFilterType;
    },
    'office/RESET_LOCAL_ORDER_IDS': (state) => {
        state.localOrderIDs = initOfficeLocalOrderIDs();
    },
    'office/RESET_OFFICE_ORDERS': (state) => {
        state.orderIDs = null;
    },
    'office/SET_BILLING_PAYMENT_ENABLED': (state, paymentEnabled) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            paymentEnabled: { $set: paymentEnabled },
        });
    },
    'office/SET_LOCAL_ORDER_ID': (state, { orderID, actionRequiredFilter }) => {
        state.localOrderIDs = update(state.localOrderIDs, {
            [actionRequiredFilter]: {
                [orderID]: { $set: initElementTimestamp(orderID) },
            },
        });
    },
    'office/SET_BILLING_PAYMENT_FORM_INIT_ERROR': (state, paymentFormInitError) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            paymentFormInitError: { $set: paymentFormInitError },
        });
    },
    'office/SET_BILLING_PAYMENT_METHOD_CHANGED': (state, paymentMethodChanged) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            paymentMethodChanged: { $set: paymentMethodChanged },
        });
    },
    'office/SET_BILLING_PAYMENT_METHOD_MODAL_IS_OPENED': (state, paymentMethodModalIsOpened) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            paymentMethodModalIsOpened: { $set: paymentMethodModalIsOpened },
        });
    },
    'office/SET_BILLING_PAYMENT_METHODS': (state, wallet) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            wallet: { $set: wallet },
        });
    },
    'office/SET_BILLING_SETTINGS_SAVING': (state, saving) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            saving: { $set: saving },
        });
    },
    'office/SET_BILLING_SETUP_CARD_ERROR_MESSAGE': (state, setupCardErrorMessage) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            setupCardErrorMessage: { $set: setupCardErrorMessage },
        });
    },
    'office/SET_BILLING_SETUP_CARD_LOADING': (state, setupCardLoading) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            setupCardLoading: { $set: setupCardLoading },
        });
    },
    'office/SET_BILLING_SETUP_INTENT_CLIENT_SECRET': (state, setupIntentClientSecret) => {
        state.localSettings.billing = update(state.localSettings.billing, {
            setupIntentClientSecret: { $set: setupIntentClientSecret },
        });
    },
    'office/SET_CLIA_ID': (state, cliaID) => {
        state.localSettings.contact = update(state.localSettings.contact, {
            cliaID: { $set: cliaID.toUpperCase() },
        });
    },
    'office/SET_CONTACT_ADDRESS': (state, location) => {
        state.localSettings.contact = update(state.localSettings.contact, {
            location: { $set: location },
        });
    },
    'office/SET_CONTACT_SETTINGS_SAVING': (state, saving) => {
        state.localSettings.contact = update(state.localSettings.contact, {
            saving: { $set: saving },
        });
    },
    'office/SET_OFFICE': (state, newOffice) => {
        state.office = newOffice;
    },
    'office/SET_OFFICE_TYPE': (state, type) => {
        if (!state.office)
            return;
        state.office.type = type;
    },
    'office/SET_OFFICE_LOAD_ERROR': (state, error) => {
        state.officeLoadError = error;
    },
    'office/SET_OFFICE_LOADING': (state, loading) => {
        state.officeLoading = loading;
    },
    'office/SET_OFFICE_NAME': (state, name) => {
        if (!state.office)
            return;
        state.localSettings.contact = update(state.localSettings.contact, {
            name: { $set: name },
        });
    },
    'office/SET_ORDERING_FORMAT': (state, orderingFormat) => {
        state.localSettings.orderFlow = update(state.localSettings.orderFlow, {
            orderingFormat: { $set: orderingFormat },
        });
    },
    'office/SET_ORDERING_PARTY': (state, orderingParty) => {
        state.localSettings.orderFlow = update(state.localSettings.orderFlow, {
            orderingParty: { $set: orderingParty },
        });
    },
    'office/SET_SCHEDULING_PARTY': (state, schedulingParty) => {
        state.localSettings.orderFlow = update(state.localSettings.orderFlow, {
            schedulingParty: { $set: schedulingParty },
        });
    },
    'office/SET_OFFICE_ORDERS': (state, orders) => {
        state.orderIDs = orders;
    },
    'office/UNSET_OFFICE_ORDER': (state, orderID) => {
        var _a, _b, _c;
        const orderIndex = (_b = (_a = state.orderIDs) === null || _a === void 0 ? void 0 : _a.indexOf(orderID)) !== null && _b !== void 0 ? _b : -1;
        state.orderIDs = update((_c = state.orderIDs) !== null && _c !== void 0 ? _c : [], { $splice: [[orderIndex, 1]] });
    },
    'office/SET_PHONE_NUMBER': (state, { phone, isValid }) => {
        state.localSettings.contact = update(state.localSettings.contact, {
            phone: { $set: phone },
            phoneValid: { $set: isValid },
        });
    },
};
const officeActions = {
    'office/bindOffice': async ({ rootState, commit, dispatch, state }, officeID) => {
        if (!state.office)
            commit('office/SET_OFFICE_LOADING', true);
        commit('office/SET_OFFICE_LOAD_ERROR', null);
        try {
            await firebaseBind(getOfficePath(officeID), (office) => {
                commit('office/SET_OFFICE', office);
                // Set office in offices module as well to support cross-app flows.
                commit('offices/SET_OFFICE', office);
                const userIsApproved = hasStatus(rootState.auth.user, UserStatus.APPROVED);
                if (userIsApproved) {
                    // TODO: Runs more than needed. Skip if lastUpdateToDenormalizedOrder
                    // changed.
                    dispatch('office/fetchOfficeDependencies');
                }
            }, { preventAutoUnbind: true });
        }
        catch (error) {
            commit('office/SET_OFFICE_LOAD_ERROR', error);
        }
        finally {
            commit('office/SET_OFFICE_LOADING', false);
        }
    },
    'office/fetchOfficeDependencies': async ({ state, dispatch }) => {
        if (isNullish(state.office))
            throw Error('Missing office');
        const officeID = state.office.id;
        await dispatch('offices/fetchOfficeDependencies', { officeID });
    },
    'office/handleSetupSucceeded': async ({ state, commit }) => {
        var _a;
        commit('office/SET_BILLING_SETUP_CARD_LOADING', true);
        try {
            const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
            if (!officeID)
                throw Error('Missing office ID when handling successful setup intent.');
            const { wallet } = await handleSetupIntentSucceededRequest({ officeID });
            if (!wallet)
                throw Error('No payment method stored.');
            commit('office/SET_BILLING_PAYMENT_METHODS', wallet);
            commit('office/SET_BILLING_PAYMENT_METHOD_MODAL_IS_OPENED', false);
            commit('office/SET_BILLING_SETUP_INTENT_CLIENT_SECRET', null);
            commit('office/SET_BILLING_PAYMENT_METHOD_CHANGED', true);
            toastSuccess('Office payment method saved.');
        }
        catch (error) {
            reportException(error);
            commit('office/SET_BILLING_SETUP_CARD_ERROR_MESSAGE', `There was a problem saving your payment method. ${errorSuffix}`);
        }
        finally {
            commit('office/SET_BILLING_SETUP_CARD_LOADING', false);
        }
    },
    'office/initBillingSettings': async ({ commit, state, rootGetters }) => {
        var _a, _b, _c, _d;
        let wallet = null;
        if ((_a = state.office) === null || _a === void 0 ? void 0 : _a.wallet) {
            wallet = await decryptData(state.office.wallet);
        }
        const labDrawTask = rootGetters['variables/getLabDrawTask'];
        const price = (_b = labDrawTask === null || labDrawTask === void 0 ? void 0 : labDrawTask.defaultPrice) !== null && _b !== void 0 ? _b : 0;
        const initialSettings = update(initOfficeBillingSettings(), {
            initialized: { $set: true },
            paymentEnabled: { $set: (_d = (_c = state.office) === null || _c === void 0 ? void 0 : _c.bookingPricing.paymentEnabled) !== null && _d !== void 0 ? _d : false },
            wallet: { $set: wallet },
            price: { $set: price },
        });
        commit('office/INIT_BILLING_SETTINGS', initialSettings);
    },
    'office/initSetupIntent': async ({ commit, state }) => {
        var _a;
        commit('office/SET_BILLING_SETUP_INTENT_CLIENT_SECRET', null);
        const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
        if (!officeID) {
            throw new Error('Missing office ID when creating setup intent.');
        }
        const setupIntentRequest = {
            officeID,
            paymentCategory: PaymentCategory.OFFICE_SETUP,
        };
        try {
            const response = await createPaymentOrSetupIntentRequest(setupIntentRequest);
            const { setupIntentClientSecret } = response;
            if (!setupIntentClientSecret) {
                throw new Error('Failed to retrieve setup intent client secret');
            }
            commit('office/SET_BILLING_SETUP_INTENT_CLIENT_SECRET', setupIntentClientSecret);
        }
        catch {
            commit('office/SET_BILLING_PAYMENT_FORM_INIT_ERROR', true);
        }
    },
    'office/saveBillingSettings': async ({ commit, dispatch, getters, state }) => {
        var _a, _b;
        const updates = {};
        const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
        if (!officeID)
            throw new Error('Missing office ID when saving billing settings.');
        if (getters['office/billingPaymentEnabledChanged']) {
            const paymentEnabled = (_b = state.localSettings.billing.paymentEnabled) !== null && _b !== void 0 ? _b : false;
            updates[`${DbRef.OFFICES}/${officeID}/bookingPricing/paymentEnabled`] = paymentEnabled;
        }
        // If only the payment method changed, the office wallet was
        // already updated.
        commit('office/SET_BILLING_SETTINGS_SAVING', true);
        try {
            await dbGroupSet(updates);
            toastSuccess('Office billing settings saved.');
            dispatch('office/initBillingSettings');
        }
        catch (error) {
            toastErrorAndReport(error);
        }
        finally {
            commit('office/SET_BILLING_SETTINGS_SAVING', false);
        }
    },
    'office/saveContactSettings': async ({ commit, getters, state }) => {
        var _a, _b, _c, _d, _e;
        const updates = {};
        const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
        if (!officeID)
            throw new Error('Missing office ID when saving contact settings.');
        if (getters['office/contactNameChanged']) {
            const newName = (_b = state.localSettings.contact.name) !== null && _b !== void 0 ? _b : null;
            updates[`${DbRef.OFFICES}/${officeID}/info/name`] = newName;
        }
        if (getters['office/contactPhoneChanged']) {
            let newPhone = null;
            if ((_c = state.localSettings.contact.phone) === null || _c === void 0 ? void 0 : _c.number) {
                newPhone = state.localSettings.contact.phone;
            }
            updates[`${DbRef.OFFICES}/${officeID}/info/phone`] = newPhone;
        }
        if (getters['office/contactLocationChanged']) {
            const newLocation = (_d = state.localSettings.contact.location) !== null && _d !== void 0 ? _d : null;
            updates[`${DbRef.OFFICES}/${officeID}/location`] = newLocation;
        }
        if (getters['office/contactCliaIDChanged']) {
            const newCliaID = (_e = state.localSettings.contact.cliaID) !== null && _e !== void 0 ? _e : null;
            updates[`${DbRef.OFFICES}/${officeID}/cliaID`] = newCliaID;
        }
        commit('office/SET_CONTACT_SETTINGS_SAVING', true);
        try {
            await dbGroupSet(updates);
            toastSuccess('Office contact settings saved.');
            // Return information for tracking. Once `trackEvent` is migrated to
            // ui-components we can submit the tracking event directly.
            return {
                officeID,
                contactSettings: state.localSettings.contact,
            };
        }
        catch (error) {
            toastErrorAndReport(error);
        }
        finally {
            commit('office/SET_CONTACT_SETTINGS_SAVING', false);
        }
    },
    'office/saveOrderFlowSettings': async ({ getters, state }) => {
        var _a;
        if (!getters['office/orderSettingsChanged'])
            return;
        try {
            const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
            if (!officeID)
                throw Error('Missing office ID when saving order flow settings.');
            const newOrderFlowSettings = {
                ...state.localSettings.orderFlow,
            };
            const orderFlowSettingsPath = `${DbRef.OFFICES}/${officeID}/orderSettings/orderFlowSettings`;
            await dbUpdate(orderFlowSettingsPath, newOrderFlowSettings);
            toastSuccess('Office contact settings saved.');
            return {
                newOrderFlowSettings,
                officeID,
            };
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
    'office/unbindOffice': ({ commit }, officeID) => {
        const path = `${DbRef.OFFICES}/${officeID}`;
        firebaseUnbind(path, { force: true });
        commit('office/RESET_OFFICE');
    },
};
const officeGetters = {
    'office/billingPaymentEnabledChanged': (state) => {
        var _a;
        return ((_a = state.office) === null || _a === void 0 ? void 0 : _a.bookingPricing.paymentEnabled)
            !== state.localSettings.billing.paymentEnabled;
    },
    'office/billingSettingsChanged': (state) => officeGetters['office/billingPaymentEnabledChanged'](state)
        || !!state.localSettings.billing.paymentMethodChanged,
    'office/billingSettingsValid': (state) => {
        var _a, _b;
        return state.localSettings.billing.paymentEnabled
            || Object.keys((_b = (_a = state.localSettings.billing.wallet) === null || _a === void 0 ? void 0 : _a.methods) !== null && _b !== void 0 ? _b : {}).length;
    },
    'office/contactCliaIDChanged': (state) => {
        var _a;
        if ((_a = state.office) === null || _a === void 0 ? void 0 : _a.cliaID) {
            return state.office.cliaID !== state.localSettings.contact.cliaID;
        }
        return !!state.localSettings.contact.cliaID;
    },
    'office/contactLocationChanged': (state) => {
        var _a, _b, _c;
        return ((_b = (_a = state.office) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.formattedAddress)
            !== ((_c = state.localSettings.contact.location) === null || _c === void 0 ? void 0 : _c.formattedAddress);
    },
    'office/contactPhoneChanged': (state) => {
        var _a, _b, _c, _d, _e, _f;
        if ((_b = (_a = state.office) === null || _a === void 0 ? void 0 : _a.info.phone) === null || _b === void 0 ? void 0 : _b.number) {
            return ((_d = (_c = state.office) === null || _c === void 0 ? void 0 : _c.info.phone) === null || _d === void 0 ? void 0 : _d.number)
                !== ((_e = state.localSettings.contact.phone) === null || _e === void 0 ? void 0 : _e.number);
        }
        return !!((_f = state.localSettings.contact.phone) === null || _f === void 0 ? void 0 : _f.number);
    },
    'office/contactSettingsChanged': (state) => officeGetters['office/contactLocationChanged'](state)
        || officeGetters['office/contactPhoneChanged'](state)
        || officeGetters['office/contactCliaIDChanged'](state)
        || officeGetters['office/contactNameChanged'](state),
    'office/contactNameChanged': (state) => {
        var _a, _b;
        return ((_a = state.office) === null || _a === void 0 ? void 0 : _a.info.name)
            ? ((_b = state.office) === null || _b === void 0 ? void 0 : _b.info.name) !== state.localSettings.contact.name
            : !!state.localSettings.contact.name;
    },
    'office/contactSettingsValid': (state) => {
        var _a;
        return state.localSettings.contact.phoneValid
            || !((_a = state.localSettings.contact.phone) === null || _a === void 0 ? void 0 : _a.number)
            || !!state.localSettings.contact.name;
    },
    'office/getLabResultsRecipients': (state) => {
        var _a;
        if (!state.office)
            return {};
        const { labResultsRecipients, prescribers } = state.office;
        const recipients = {};
        return (_a = Object.keys(prescribers !== null && prescribers !== void 0 ? prescribers : {})) === null || _a === void 0 ? void 0 : _a.reduce((acc, prescriberID) => {
            var _a;
            const prescriberLabResultsRecipients = (_a = labResultsRecipients === null || labResultsRecipients === void 0 ? void 0 : labResultsRecipients[prescriberID]) !== null && _a !== void 0 ? _a : initLabResultsRecipients(prescriberID);
            acc[prescriberID] = prescriberLabResultsRecipients;
            return acc;
        }, recipients);
    },
    'office/getOfficePrescriberOptions': (state, _getters, rootState) => {
        var _a;
        const prescribers = (_a = state.office) === null || _a === void 0 ? void 0 : _a.prescribers;
        if (!prescribers)
            return [];
        return Object.values(prescribers)
            .map((prescriber) => {
            var _a;
            const staffUser = (_a = rootState.users.staffUsers) === null || _a === void 0 ? void 0 : _a[prescriber.id];
            if (!staffUser)
                return;
            return {
                id: staffUser.id,
                label: formatFullName(staffUser),
                info: staffUser.info,
            };
        })
            .filter(nullishFilter);
    },
    'office/getOfficeStaffUserOptions': (state, _getters, rootState) => {
        var _a;
        const staffUsers = Object.values((_a = rootState.users.staffUsers) !== null && _a !== void 0 ? _a : {});
        return staffUsers.map((staffUser) => ({
            id: staffUser.id,
            label: formatFullName(staffUser),
            info: staffUser.info,
        })).filter(nullishFilter);
    },
    'office/isThePrescriberOrInitiator': (state, _getters, rootState) => (orderID, _order) => {
        var _a;
        const order = _order !== null && _order !== void 0 ? _order : rootState.orders.orders[orderID];
        const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
        const officeIsInitiator = (order === null || order === void 0 ? void 0 : order.createdByOfficeID) === officeID;
        const officeIsPrescriber = (order === null || order === void 0 ? void 0 : order.officeID) === officeID;
        return officeIsInitiator || officeIsPrescriber;
    },
    'office/orderSettingsChanged': (state) => officeGetters['office/orderingFormatChanged'](state)
        || officeGetters['office/orderingPartyChanged'](state)
        || officeGetters['office/defaultOfficeSchedulingUserIDChanged'](state)
        || officeGetters['office/schedulingPartyChanged'](state),
    'office/orderingFormatChanged': (state) => {
        var _a;
        const officeOrderingFormat = (_a = state.office) === null || _a === void 0 ? void 0 : _a.orderSettings.orderFlowSettings.orderingFormat;
        const localOrderingFormat = state.localSettings.orderFlow.orderingFormat;
        return officeOrderingFormat !== localOrderingFormat;
    },
    'office/orderingPartyChanged': (state) => {
        var _a;
        const officeOrderingParty = (_a = state.office) === null || _a === void 0 ? void 0 : _a.orderSettings.orderFlowSettings.orderingParty;
        const localOrderingParty = state.localSettings.orderFlow.orderingParty;
        return officeOrderingParty !== localOrderingParty;
    },
    'office/defaultOfficeSchedulingUserIDChanged': (state) => {
        var _a;
        const officeDefaultUserID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.orderSettings.orderFlowSettings.defaultOfficeSchedulingUserID;
        const localDefaultUserID = state.localSettings.orderFlow.defaultOfficeSchedulingUserID;
        return officeDefaultUserID !== localDefaultUserID;
    },
    'office/getLocalOrderIDs': (state) => {
        var _a;
        const { actionRequiredFilter } = state.algolia.orders;
        const localOrderIDs = Object.values((_a = state.localOrderIDs[actionRequiredFilter]) !== null && _a !== void 0 ? _a : [])
            .sort((a, b) => a.timestamp - b.timestamp)
            .map(({ id }) => id);
        return localOrderIDs;
    },
    'office/getPatientOptions': (state, _getters, rootState) => {
        var _a, _b, _c, _d, _e;
        const officeID = (_a = state.office) === null || _a === void 0 ? void 0 : _a.id;
        if (!officeID)
            return [];
        if (((_b = state.office) === null || _b === void 0 ? void 0 : _b.type) === OfficeType.LAB) {
            return Object.values(Object.values((_d = (_c = state.office) === null || _c === void 0 ? void 0 : _c.partners) !== null && _d !== void 0 ? _d : {}).reduce((acc, office) => {
                var _a;
                return ({
                    ...acc,
                    ...arrayToObj((_a = rootState.offices.patientOptionsByOffice[office.officeID]) !== null && _a !== void 0 ? _a : [], 'id'),
                });
            }, {}));
        }
        return (_e = rootState.offices.patientOptionsByOffice[officeID]) !== null && _e !== void 0 ? _e : [];
    },
    'office/getOfficeOrderIDs': (state) => {
        var _a;
        const algoliaOrderIDs = (_a = state.orderIDs) !== null && _a !== void 0 ? _a : null;
        const localOrderIDs = officeGetters['office/getLocalOrderIDs'](state);
        if (localOrderIDs.length === 0)
            return algoliaOrderIDs;
        return deduplicateArray(localOrderIDs.concat(algoliaOrderIDs !== null && algoliaOrderIDs !== void 0 ? algoliaOrderIDs : []));
    },
    'office/getOfficePartnerReferences': (state) => {
        var _a;
        if (!state.office)
            return [];
        return Object.values((_a = state.office.partners) !== null && _a !== void 0 ? _a : {});
    },
    'office/getIsOfficeType': (state) => (type) => { var _a; return ((_a = state.office) === null || _a === void 0 ? void 0 : _a.type) === type; },
    'office/schedulingPartyChanged': (state) => {
        var _a;
        const officeSchedulingParty = (_a = state.office) === null || _a === void 0 ? void 0 : _a.orderSettings.orderFlowSettings.schedulingParty;
        const localSchedulingParty = state.localSettings.orderFlow.schedulingParty;
        return officeSchedulingParty !== localSchedulingParty;
    },
};
const officeModule = {
    state: officeState,
    mutations: officeMutations,
    actions: officeActions,
    getters: officeGetters,
};
const getOfficeModule = () => ({
    state: officeState,
    mutations: officeMutations,
    actions: officeActions,
    getters: officeGetters,
});
const initOfficeModule = (moduleExtensions) => initExtendedModule(getOfficeModule(), moduleExtensions);

export { getOfficeModule, initOfficeModule, officeModule };
